import {
  CssBaseline,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import { usePageContext } from 'vike-react/usePageContext'; // or vike-{vue,solid}

import LoginModal from '../../components/authentication/LoginModal';
import LoadingCover from '../../components/ui/LoadingCover';
import { appConfig } from '../../config/runtimeConfig';
import { ConsentProvider } from '../../context/ConsentContext';
import { HeadOwnedByServerContext } from '../../context/HeadOwnedByServerContext';
import { useSessionBootstrap } from '../../hooks/useSessionBootstrap';
import { AnalyticsProvider } from '../../providers/AnalyticsProvider';
import { makeStore } from '../../redux/store';
import { theme } from '../../theme/theme';

// This page's Router only knows about its own bookable route - unlike
// App.tsx's Router, which has every route in the app. Client-side
// navigation to anywhere else (e.g. clicking through to
// /confirm-booking/:id after submitting the booking form) would otherwise
// match nothing and render a blank page. Force a real browser navigation
// instead: the request then goes back through Vike's server routing, which
// falls through to the SPA catch-all (see src/pages/+route.ts) and mounts
// the full App.tsx with every route.
function ExitToFullApp() {
  const location = useLocation();
  useEffect(() => {
    const target = `${location.pathname}${location.search}${location.hash}`;
    window.location.href = target;
    // Safety net: react-router's navigate() already updates
    // window.location via history.pushState before this effect runs, so
    // assigning the (now-identical) href above can be a same-URL no-op in
    // some browsers/timings instead of a real navigation - stuck on this
    // loading cover until a manual refresh (reported from e.g. clicking a
    // cross-bookable-type card in a Recommended carousel). If we're still
    // here after a beat, force it. Harmless when the href assignment did
    // navigate: the whole document, this timer included, is gone before
    // it can fire.
    const fallback = window.setTimeout(() => {
      window.location.reload();
    }, 300);
    return () => window.clearTimeout(fallback);
  }, [location]);
  return <LoadingCover />;
}

// useSessionBootstrap() needs Redux context, so it can't be called directly
// in createBookablePage's own Page function - that function is what CREATES
// the <Provider> below, and isn't itself inside it. A no-render child
// component gets it inside that Provider, same as App.tsx's own App
// component sits inside the <Provider> its Wrapper creates.
function SessionBootstrap() {
  useSessionBootstrap();
  return null;
}

// Shared `+Page.tsx` body for every bookable detail page - see
// src/pages/boat/+Page.tsx (etc.) for the thin per-type wrapper. Also reused
// as-is for the homepage (src/pages/home/+Page.tsx): the SSR shell (router +
// providers + ExitToFullApp escape hatch for links outside routePath) is
// identical for any single-route SSR page, bookable or not.
export function createBookablePage(
  routePath: string,
  PageComponent: React.ComponentType
) {
  return function Page() {
    LicenseInfo.setLicenseKey(appConfig.APP_MUI_X_KEY);
    const pageContext = usePageContext();
    const isServer = typeof window === 'undefined';
    const Router: React.ComponentType<any> = isServer
      ? StaticRouter
      : BrowserRouter;
    // Full original URL (path + query string, e.g. a `?_c=` discount code)
    // — StaticRouter needs it to resolve the same route+params the client
    // would, and the page component reads the discount code from it.
    //
    // `pageContext.urlOriginal` is documented as "the URL as-provided to
    // renderPage()" - in production that's request.raw.url (path-only, per
    // server/index.ts), but Vike's own dev-mode middleware (`npm start` /
    // `vite dev`) constructs it as a full origin-qualified URL instead
    // (e.g. "http://localhost:3001/boat/x"). StaticRouter's `location`
    // expects a path, not a URL - handed the dev-mode value as-is, no
    // <Route> ever matches ("No routes matched location ..."), so the SSR
    // pass renders only the `*` fallback and the client then hydrates a
    // mismatched tree. Normalising through `new URL()` (which accepts an
    // absolute URL as-is, or resolves a relative one against the second
    // arg) makes this work the same regardless of which shape Vike hands
    // us, in dev or prod.
    const toPath = (url: string) => {
      const parsed = new URL(url, 'http://ssr-placeholder.invalid');
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    };
    const routerProps = isServer
      ? { location: toPath(pageContext.urlOriginal) }
      : { location: pageContext.urlParsed };

    // Create a per-request/per-render store; no deps needed to avoid
    // unnecessary re-creation
    const store = React.useMemo(
      () => makeStore((pageContext.data as any).initialReduxState),
      [pageContext.data]
    );

    return (
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <ConsentProvider>
            <StyledEngineProvider injectFirst>
              <CssBaseline />
              <HelmetProvider>
                {/* This page's +Head.tsx server-renders its <head> tags, so
                PageMeta stays out of it (EM-1183). */}
                <HeadOwnedByServerContext.Provider value={true}>
                  <AnalyticsProvider>
                    <Router {...routerProps}>
                      <SessionBootstrap />
                      <Routes>
                        <Route path={routePath} element={<PageComponent />} />
                        <Route path="*" element={<ExitToFullApp />} />
                      </Routes>
                      {/* Mounted once per App.tsx's own placement (alongside
                    its <Routes>) - without it, header buttons that open it
                    via Redux state (e.g. HeaderNav's login-button) dispatch
                    into a void, since this page's tree is otherwise just
                    PageComponent, not the full App.tsx. */}
                      <LoginModal />
                    </Router>
                  </AnalyticsProvider>
                </HeadOwnedByServerContext.Provider>
              </HelmetProvider>
            </StyledEngineProvider>
          </ConsentProvider>
        </ThemeProvider>
      </Provider>
    );
  };
}
